// Px3 Pipeline - one order, in full.
//
// Owns all server state for the order (detail + events) and polls at 3s while it
// is still moving, the same cadence as ForeignDnsRunDetail and the Sequencer
// Connect progress view. A terminal order stops polling: nothing will change it
// again except an action taken here, and every action reloads.
//
// Three things this screen refuses to do:
//
//  1. Offer an action the engine would refuse. The buttons are gated on
//     `order.status` with the SAME rules the engine enforces (spec 5.7), so a
//     click that reaches the server is a click that can work. The engine still
//     has the last word - it answers 409 action_refused and that is surfaced
//     verbatim - but the operator should not have to discover the rules by
//     being told no.
//  2. Paint a stamp it did not see. Every stamp cell is a tick only when the
//     timestamp is non-null; "not yet" is a dash, never a blank and never a
//     tick. The date lives in the tooltip so eight stamp columns still fit.
//  3. Hide a password behind a mask. Cleartext is the house convention for
//     operator views (spec 5.8): someone debugging a failed IMAP login needs the
//     password the pipeline actually set. Storage stays encrypted; the value is
//     fetched on demand, one mailbox at a time, and a purged order says so
//     rather than showing an empty string.

import { useCallback, useEffect, useRef, useState } from 'react';

import type { Px3ShowToast } from '../Px3';
import {
  px3Api,
  px3ErrorMessage,
  type Px3Action,
  type Px3ActionResult,
  type Px3Event,
  type Px3Order,
  type Px3OrderDetail,
  type Px3OrderStatus,
} from '../px3Api';
import { ago } from './OrdersList';
import { StageRail } from './StageRail';

/** Same 3s as the other live detail screens. */
const POLL_MS = 3000;

const EVENT_LIMIT = 300;

/** How many events the Callback tab falls back to when none of them mention a
 *  callback (an order with no callback_url never logs one). */
const CALLBACK_FALLBACK_EVENTS = 20;

/** Nothing moves an order in one of these again. Polling stops; the Refresh
 *  button stays, because an action taken here still changes the row. */
const TERMINAL: ReadonlySet<Px3OrderStatus> = new Set<Px3OrderStatus>([
  'completed',
  'failed',
  'cancelled',
  'dry_run_complete',
]);

const STATUS_LABEL: Record<Px3OrderStatus, string> = {
  queued: 'queued',
  running: 'running',
  awaiting_capacity: 'awaiting capacity',
  needs_attention: 'needs attention',
  completed: 'completed',
  failed: 'failed',
  cancelled: 'cancelled',
  dry_run_complete: 'dry run done',
};

type DetailTab = 'domains' | 'mailboxes' | 'events' | 'preflight' | 'callback';

const TABS: { id: DetailTab; label: string }[] = [
  { id: 'domains', label: 'Domains' },
  { id: 'mailboxes', label: 'Mailboxes' },
  { id: 'events', label: 'Events' },
  { id: 'preflight', label: 'Preflight' },
  { id: 'callback', label: 'Callback' },
];

/**
 * The actions that need a confirm, and what the modal says.
 *
 * Abort and Continue are here because the brief asks for them and because both
 * are one-way: Abort ends the order, Continue writes off failed items as
 * abandoned and they are never revisited. Purge secrets joins them for the same
 * reason - it drops the customer's keys and every mailbox password, and on an
 * order that is still running that is unrecoverable without a fresh submit.
 */
const CONFIRM: Partial<Record<Px3Action, { title: string; body: string; cta: string }>> = {
  abort: {
    title: 'Abort this order?',
    body: 'The order goes to cancelled and units still in flight stop when they next check the status. Nothing already built is torn down - the final callback lists what exists. This cannot be undone.',
    cta: 'Abort the order',
  },
  continue: {
    title: 'Continue past the failures?',
    body: "Every failed item in the current stage is marked abandoned and the order moves on without it. Abandoned items are never retried and count as failed in every later report, including the customer's callback. Retry failed is the reversible option.",
    cta: 'Abandon them and continue',
  },
  'purge-secrets': {
    title: 'Purge the borrowed secrets now?',
    body: 'The registrar key, sequencer key, MasterInbox key, callback token, every mailbox password and every stored photo are dropped. If the order is still running, the stages that need them will fail and the only fix is a new order.',
    cta: 'Purge them now',
  },
};

const ACTION_LABEL: Record<Px3Action, string> = {
  'retry-failed': 'Retry failed',
  continue: 'Continue anyway',
  abort: 'Abort',
  'resend-callback': 'Resend callback',
  'rerun-preflight': 'Re-run preflight',
  'purge-secrets': 'Purge secrets',
};

function fmtDate(iso: string | null): string {
  return iso
    ? new Date(iso).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
    : '-';
}

/** A stamp cell: a tick when the stage stamped it, a dash when it has not run
 *  yet. Both carry the label and the exact time in the tooltip, which is the
 *  only place eight or nine timestamps per row can live and stay readable. */
function Stamp({ label, at }: { label: string; at: string | null }): JSX.Element {
  return at ? (
    <span className="px3-stamp" title={`${label}: ${fmtDate(at)}`}>
      {'\u2713'}
    </span>
  ) : (
    <span className="px3-stamp px3-stamp--off" title={`${label}: not yet`}>
      -
    </span>
  );
}

/** `conflicts` is free-form JSON written by the dns_records stage. Count what is
 *  countable and say nothing when there is nothing - a "0" in every row would
 *  train the eye to skip the column that matters. */
function conflictCount(conflicts: unknown): number {
  if (Array.isArray(conflicts)) return conflicts.length;
  if (conflicts !== null && typeof conflicts === 'object') return Object.keys(conflicts).length;
  return 0;
}

function pretty(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function MetaItem({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="px3-meta-item">
      <span className="px3-meta-label">{label}</span>
      <span className="px3-meta-value">{value}</span>
    </div>
  );
}

/** Which actions are offered in which status (spec 5.7). Mirrors the engine's
 *  own gates; the engine remains the authority and answers 409 if this is ever
 *  wrong, which is surfaced rather than swallowed. */
function actionsFor(order: Px3Order): Px3Action[] {
  const out: Px3Action[] = [];
  if (order.status === 'needs_attention') out.push('retry-failed', 'continue');
  if (
    order.status === 'needs_attention' ||
    order.status === 'running' ||
    order.status === 'awaiting_capacity' ||
    order.status === 'queued'
  ) {
    out.push('abort');
  }
  if (order.status === 'awaiting_capacity' || order.status === 'failed') out.push('rerun-preflight');
  if (order.callback_url !== null) out.push('resend-callback');
  if (order.secrets_purged_at === null) out.push('purge-secrets');
  return out;
}

/** One line of operator-facing English per action result, built from the counts
 *  the server actually reported. "Done" would hide a retry that moved nothing. */
function describeResult(action: Px3Action, result: Px3ActionResult): string {
  switch (action) {
    case 'retry-failed':
      return `Retry queued: ${result.domains ?? 0} domain(s) and ${result.mailboxes ?? 0} mailbox(es) back to pending.`;
    case 'continue':
      return `Continuing: ${result.domains ?? 0} domain(s) and ${result.mailboxes ?? 0} mailbox(es) abandoned.`;
    case 'abort':
      return 'Order aborted. In-flight units stop when they next check the status.';
    case 'resend-callback':
      return `Callback queued: ${result.event ?? 'sent'}.`;
    case 'rerun-preflight':
      return 'Preflight re-queued. The order is back at stage preflight.';
    case 'purge-secrets':
      return 'Secrets purged. Mailbox passwords are no longer readable for this order.';
  }
}

export function OrderDetail({
  orderId,
  onBack,
  showToast,
}: {
  orderId: string;
  onBack: () => void;
  showToast: Px3ShowToast;
}): JSX.Element {
  const [detail, setDetail] = useState<Px3OrderDetail | null>(null);
  const [events, setEvents] = useState<Px3Event[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [tab, setTab] = useState<DetailTab>('domains');
  const [busy, setBusy] = useState<Px3Action | null>(null);
  const [confirming, setConfirming] = useState<Px3Action | null>(null);

  /** email -> revealed value. `null` means the endpoint answered null, which is
   *  a purged order, not an empty password. */
  const [passwords, setPasswords] = useState<Record<string, string | null>>({});
  const [revealing, setRevealing] = useState<string | null>(null);

  /** Monotonic request id, so a slow poll cannot overwrite a newer read - the
   *  reload fired right after an action is exactly the one that must win. */
  const reqId = useRef(0);

  const load = useCallback(async (): Promise<void> => {
    const mine = ++reqId.current;
    try {
      const fresh = await px3Api.getOrder(orderId);
      if (mine !== reqId.current) return;
      setDetail(fresh);
      setLoadError(null);
    } catch (err) {
      if (mine !== reqId.current) return;
      setLoadError(px3ErrorMessage(err));
    }
  }, [orderId]);

  /** Events are fetched only for the two tabs that show them. 300 rows every 3s
   *  behind a table nobody is looking at is a read the database does not owe us. */
  const wantEvents = tab === 'events' || tab === 'callback';

  const loadEvents = useCallback(async (): Promise<void> => {
    try {
      setEvents(await px3Api.listEvents(orderId, EVENT_LIMIT));
    } catch {
      // The event feed is secondary: a failed read leaves the last good list on
      // screen rather than replacing the order view with an error. The detail
      // load above is the one whose failure is reported.
    }
  }, [orderId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!wantEvents) return;
    void loadEvents();
  }, [wantEvents, loadEvents]);

  /**
   * Poll while the order is still moving - and while the first read has not
   * landed at all. A transient 500 on open used to leave this screen dead
   * (nothing was polling, and the only button went back to the list); now the
   * same 3s tick keeps trying and the screen fills itself in when the server
   * answers.
   */
  const live = detail === null || !TERMINAL.has(detail.order.status);

  useEffect(() => {
    if (!live) return;
    const timer = window.setInterval(() => {
      void load();
      if (wantEvents) void loadEvents();
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [live, load, loadEvents, wantEvents]);

  async function runAction(action: Px3Action): Promise<void> {
    setBusy(action);
    setConfirming(null);
    try {
      const result = await px3Api.action(orderId, action);
      // A revealed password is dead the moment the secrets go: the endpoint
      // answers null from here on, and leaving the old value on screen would
      // show a credential this order can no longer produce.
      if (action === 'purge-secrets') setPasswords({});
      showToast(action === 'continue' || action === 'purge-secrets' ? 'warn' : 'success',
        describeResult(action, result));
      await load();
      if (wantEvents) await loadEvents();
    } catch (err) {
      showToast('error', px3ErrorMessage(err));
    } finally {
      setBusy(null);
    }
  }

  async function reveal(email: string): Promise<void> {
    setRevealing(email);
    try {
      const { password } = await px3Api.getMailboxPassword(orderId, email);
      setPasswords((prev) => ({ ...prev, [email]: password }));
    } catch (err) {
      showToast('error', px3ErrorMessage(err));
    } finally {
      setRevealing(null);
    }
  }

  if (!detail) {
    return (
      <>
        <div className="px3-detailhead">
          <button type="button" className="px3-btn px3-btn--secondary px3-btn--small" onClick={onBack}>
            Back to orders
          </button>
          {loadError !== null && (
            <button
              type="button"
              className="px3-btn px3-btn--ghost px3-btn--small"
              onClick={() => void load()}
            >
              Retry
            </button>
          )}
          <span className="px3-pulse" title="Retrying every 3 seconds" />
        </div>
        <section className="px3-card">
          <div className="px3-empty">
            {loadError === null
              ? 'Loading order...'
              : `Could not load this order: ${loadError}. Retrying every 3 seconds - Retry asks again now.`}
          </div>
        </section>
      </>
    );
  }

  const { order, domains, mailboxes, progress } = detail;
  const available = actionsFor(order);
  const confirmSpec = confirming ? CONFIRM[confirming] : undefined;

  const callbackEvents = events.filter((e) => e.message.toLowerCase().includes('callback'));
  const showingFallback = callbackEvents.length === 0;

  return (
    <>
      <div className="px3-detailhead">
        <button type="button" className="px3-btn px3-btn--secondary px3-btn--small" onClick={onBack}>
          Back to orders
        </button>
        <h1 className="px3-title">{order.customer}</h1>
        <span className={`px3-badge px3-badge--${order.status}`}>{STATUS_LABEL[order.status]}</span>
        <span className="px3-badge px3-badge--muted">{order.stage}</span>
        {order.dry_run && <span className="px3-badge px3-badge--muted">dry run</span>}
        {live && <span className="px3-pulse" title="Polling every 3 seconds" />}
      </div>

      <div className="px3-meta">
        <MetaItem label="External ref" value={order.external_ref} />
        <MetaItem label="Source" value={order.source_name ?? 'admin (dashboard submit)'} />
        <MetaItem label="Age" value={ago(order.created_at)} />
        <MetaItem label="Created" value={fmtDate(order.created_at)} />
        <MetaItem label="Updated" value={fmtDate(order.updated_at)} />
        <MetaItem
          label="Size"
          value={`${order.domains_total} domains, ${order.mailboxes_total} mailboxes (${order.users_per_domain}/domain)`}
        />
        <MetaItem label="Sequencer" value={`${order.sequencer_provider} / ${order.sequencer_profile}`} />
        <MetaItem
          label="MasterInbox"
          value={
            order.has_masterinbox
              ? `workspace ${order.masterinbox_workspace_id ?? '?'} / app ${order.masterinbox_app_id ?? '?'}`
              : 'not requested'
          }
        />
        <MetaItem label="Cloudflare account" value={order.cf_account_id} />
        <MetaItem label="Forwarding" value={order.forwarding_target_url ?? 'none'} />
        <MetaItem
          label="Secrets"
          value={order.secrets_purged_at ? `purged ${fmtDate(order.secrets_purged_at)}` : 'held'}
        />
      </div>

      {loadError !== null && (
        <div className="px3-toast px3-toast--error">
          The last refresh failed ({loadError}). What is on screen is the last good read.
        </div>
      )}

      {order.attention_reason && (
        <div className="px3-card">
          <div className="px3-card-body">
            <div className="px3-notice px3-notice--warn">
              <strong>Waiting on you:</strong> {order.attention_reason}
            </div>
          </div>
        </div>
      )}

      {order.error_code && (
        <div className="px3-card">
          <div className="px3-card-body">
            <div className="px3-notice px3-notice--err">
              <strong>{order.error_code}</strong>
              {order.error_message ? ` - ${order.error_message}` : ''}
            </div>
          </div>
        </div>
      )}

      <section className="px3-card">
        <div className="px3-card-header">
          <h2 className="px3-card-title">Actions</h2>
          <div className="px3-actions">
            <button
              type="button"
              className="px3-btn px3-btn--ghost px3-btn--small"
              onClick={() => {
                void load();
                if (wantEvents) void loadEvents();
              }}
            >
              Refresh
            </button>
          </div>
        </div>
        <div className="px3-card-body">
          <div className="px3-actions">
            {available.length === 0 ? (
              <span className="px3-hint">
                Nothing to do here: this order is {STATUS_LABEL[order.status]} and its secrets are
                already purged.
              </span>
            ) : (
              available.map((action) => (
                <button
                  key={action}
                  type="button"
                  className={`px3-btn ${
                    action === 'abort' || action === 'purge-secrets'
                      ? 'px3-btn--danger'
                      : action === 'retry-failed'
                        ? 'px3-btn--primary'
                        : 'px3-btn--secondary'
                  }`}
                  disabled={busy !== null}
                  onClick={() =>
                    CONFIRM[action] ? setConfirming(action) : void runAction(action)
                  }
                >
                  {busy === action ? 'Working...' : ACTION_LABEL[action]}
                </button>
              ))
            )}
          </div>
        </div>
      </section>

      <section className="px3-card">
        <div className="px3-card-header">
          <h2 className="px3-card-title">
            Stages
            {live && <span className="px3-pulse" />}
          </h2>
        </div>
        <div className="px3-card-body">
          <StageRail progress={progress} />
        </div>
      </section>

      <div className="px3-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`px3-tab ${tab === t.id ? 'active' : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
            {t.id === 'domains' ? ` (${domains.length})` : ''}
            {t.id === 'mailboxes' ? ` (${mailboxes.length})` : ''}
          </button>
        ))}
      </div>

      {tab === 'domains' && (
        <section className="px3-card">
          <div className="px3-table-wrap">
            <table className="px3-table">
              <thead>
                <tr>
                  <th>Domain</th>
                  <th>Panel</th>
                  <th>Zone</th>
                  <th>Nameservers</th>
                  <th className="px3-col-center">Stamps</th>
                  <th className="px3-col-center">DKIM</th>
                  <th className="px3-col-center">Conflicts</th>
                  <th className="px3-col-center">Status</th>
                  <th>Error</th>
                </tr>
              </thead>
              <tbody>
                {domains.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px3-empty">
                      No domains on this order yet - they are written at intake, so an empty table
                      here means the order row is incomplete.
                    </td>
                  </tr>
                ) : (
                  domains.map((d) => {
                    const conflicts = conflictCount(d.conflicts);
                    return (
                      <tr
                        key={d.domain}
                        className={
                          d.status === 'error' || d.status === 'abandoned' ? 'px3-row--bad' : ''
                        }
                      >
                        <td className="px3-cell-mono">{d.domain}</td>
                        <td className="px3-cell-mono">{d.panel_email ?? '-'}</td>
                        <td className="px3-cell-mono" title={d.cf_zone_id ?? 'no zone yet'}>
                          {d.cf_zone_id ? d.cf_zone_id.slice(0, 8) : '-'}
                        </td>
                        <td className="px3-cell-faint" title={`${d.ns1 ?? '-'} / ${d.ns2 ?? '-'}`}>
                          {d.ns1 ? d.ns1.split('.')[0] : '-'}
                          {d.ns2 ? ` / ${d.ns2.split('.')[0]}` : ''}
                        </td>
                        <td className="px3-col-center">
                          <span className="px3-stamps">
                            <Stamp label="zone created" at={d.zone_created_at} />
                            <Stamp label="NS set at registrar" at={d.ns_set_at} />
                            <Stamp label="zone active" at={d.zone_active_at} />
                            <Stamp label="records written" at={d.records_written_at} />
                            <Stamp label="forwarding written" at={d.forwarding_written_at} />
                            <Stamp label="added to Google" at={d.google_added_at} />
                            <Stamp label="verified on Google" at={d.google_verified_at} />
                            <Stamp label="DKIM" at={d.dkim_at} />
                          </span>
                        </td>
                        <td className="px3-col-center px3-cell-faint">{d.dkim_status ?? '-'}</td>
                        <td className="px3-col-center">
                          {conflicts > 0 ? (
                            <span
                              className="px3-badge px3-badge--warn"
                              title={pretty(d.conflicts)}
                            >
                              {conflicts}
                            </span>
                          ) : (
                            <span className="px3-cell-faint">-</span>
                          )}
                        </td>
                        <td className="px3-col-center">
                          <span className={`px3-badge px3-badge--${d.status}`}>{d.status}</span>
                        </td>
                        <td className={d.error_code ? 'px3-cell-error' : 'px3-cell-faint'}>
                          {d.error_code
                            ? `${d.error_stage ? `[${d.error_stage}] ` : ''}${d.error_code}${
                                d.error_message ? `: ${d.error_message}` : ''
                              }`
                            : '-'}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tab === 'mailboxes' && (
        <section className="px3-card">
          <div className="px3-card-header">
            <h2 className="px3-card-title">Mailboxes</h2>
            <span className="px3-hint">
              Stamps in order: claim, licence, rename, alias stripped, photo, IMAPs push, login
              seen, sequencer, MasterInbox. Hover any of them for the exact time.
            </span>
          </div>
          <div className="px3-table-wrap">
            <table className="px3-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Panel</th>
                  <th>Holding</th>
                  <th className="px3-col-center">Group</th>
                  <th className="px3-col-center">Stamps</th>
                  <th className="px3-col-center">Status</th>
                  <th>Error</th>
                  <th>Password</th>
                </tr>
              </thead>
              <tbody>
                {mailboxes.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px3-empty">
                      No mailboxes on this order.
                    </td>
                  </tr>
                ) : (
                  mailboxes.map((m) => {
                    const revealed = Object.prototype.hasOwnProperty.call(passwords, m.email);
                    const errors = [
                      m.error_code
                        ? `${m.error_stage ? `[${m.error_stage}] ` : ''}${m.error_code}${
                            m.error_message ? `: ${m.error_message}` : ''
                          }`
                        : '',
                      m.sequencer_error ? `sequencer: ${m.sequencer_error}` : '',
                      m.masterinbox_error ? `masterinbox: ${m.masterinbox_error}` : '',
                    ].filter((s) => s !== '');
                    return (
                      <tr
                        key={m.email}
                        className={
                          m.status === 'error' || m.status === 'abandoned' ? 'px3-row--bad' : ''
                        }
                      >
                        <td className="px3-cell-mono">{m.email}</td>
                        <td className="px3-cell-mono">{m.panel_email ?? '-'}</td>
                        <td
                          className="px3-cell-mono px3-cell-faint px3-cell-trunc"
                          title={m.holding_email ?? 'no holding address yet'}
                        >
                          {m.holding_email ?? '-'}
                        </td>
                        <td className="px3-col-center px3-cell-faint">{m.group_label ?? '-'}</td>
                        <td className="px3-col-center">
                          <span className="px3-stamps">
                            <Stamp label="claimed" at={m.claimed_at} />
                            <Stamp label="licensed" at={m.licensed_at} />
                            <Stamp label="renamed" at={m.renamed_at} />
                            <Stamp label="alias stripped" at={m.alias_stripped_at} />
                            <Stamp
                              label={`photo (${m.photo_status})`}
                              at={m.photo_at}
                            />
                            <Stamp label="IMAPs pushed" at={m.imaps_pushed_at} />
                            <Stamp label="login verified" at={m.login_verified_at} />
                            <Stamp
                              label={`sequencer${m.sequencer_status ? ` (${m.sequencer_status})` : ''}`}
                              at={m.sequencer_connected_at}
                            />
                            <Stamp label="masterinbox" at={m.masterinbox_connected_at} />
                          </span>
                        </td>
                        <td className="px3-col-center">
                          <span className={`px3-badge px3-badge--${m.status}`}>{m.status}</span>
                        </td>
                        <td className={errors.length > 0 ? 'px3-cell-error' : 'px3-cell-faint'}>
                          {errors.length > 0 ? errors.join(' | ') : '-'}
                        </td>
                        <td>
                          {revealed ? (
                            passwords[m.email] === null ? (
                              <span className="px3-secret px3-secret--gone">
                                purged - no longer readable
                              </span>
                            ) : (
                              <span className="px3-secret">{passwords[m.email]}</span>
                            )
                          ) : (
                            <button
                              type="button"
                              className="px3-btn px3-btn--ghost px3-btn--small"
                              disabled={revealing === m.email}
                              onClick={() => void reveal(m.email)}
                            >
                              {revealing === m.email ? 'Reading...' : 'Reveal password'}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tab === 'events' && (
        <section className="px3-card">
          <div className="px3-card-header">
            <h2 className="px3-card-title">Events ({events.length})</h2>
            <span className="px3-hint">Newest first. Every operator action is logged here too.</span>
          </div>
          <div className="px3-card-body">
            {events.length === 0 ? (
              <div className="px3-empty">No events yet.</div>
            ) : (
              <div className="px3-events">
                {events.map((e) => (
                  <div key={e.id} className={`px3-event px3-event--${e.level}`}>
                    <span className="px3-event-ts">{fmtDate(e.ts)}</span>
                    <span className="px3-event-stage">{e.stage ?? '-'}</span>
                    <span className="px3-event-msg">{e.message}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {tab === 'preflight' && (
        <section className="px3-card">
          <div className="px3-card-header">
            <h2 className="px3-card-title">Preflight report</h2>
          </div>
          <div className="px3-card-body">
            {order.preflight === null ? (
              <div className="px3-notice">
                Preflight has not written a report yet. It lands when the stage finishes, and it
                carries the external checks plus the panel allocation the rest of the run uses.
              </div>
            ) : (
              <pre className="px3-pre">{pretty(order.preflight)}</pre>
            )}
          </div>
        </section>
      )}

      {tab === 'callback' && (
        <section className="px3-card">
          <div className="px3-card-header">
            <h2 className="px3-card-title">Callback</h2>
            <div className="px3-actions">
              {order.callback_url !== null && (
                <button
                  type="button"
                  className="px3-btn px3-btn--secondary px3-btn--small"
                  disabled={busy !== null}
                  onClick={() => void runAction('resend-callback')}
                >
                  {busy === 'resend-callback' ? 'Queueing...' : 'Resend callback'}
                </button>
              )}
            </div>
          </div>
          <div className="px3-card-body">
            {order.callback_url === null ? (
              <div className="px3-notice">
                This order carries no callback URL, so nothing is ever posted back. The portal reads
                its status by polling instead.
              </div>
            ) : (
              <>
                <div className="px3-meta" style={{ padding: 0 }}>
                  <MetaItem label="URL" value={order.callback_url} />
                  <MetaItem label="Last seq" value={String(order.callback_seq)} />
                  <MetaItem
                    label="Bearer token"
                    value={order.has_callback_token ? 'held' : 'purged'}
                  />
                </div>
                {!order.has_callback_token && (
                  <div className="px3-notice px3-notice--warn">
                    The callback token has been purged, so a resend would go out unauthenticated and
                    the portal will reject it. Resend is only useful before the purge.
                  </div>
                )}
              </>
            )}

            <h3 className="px3-card-title">
              {showingFallback ? `Last ${CALLBACK_FALLBACK_EVENTS} events` : 'Callback events'}
            </h3>
            {showingFallback && events.length > 0 && (
              <span className="px3-hint">
                No event on this order mentions a callback yet, so the most recent activity is shown
                instead.
              </span>
            )}
            {events.length === 0 ? (
              <div className="px3-empty">No events yet.</div>
            ) : (
              <div className="px3-events">
                {(showingFallback ? events.slice(0, CALLBACK_FALLBACK_EVENTS) : callbackEvents).map(
                  (e) => (
                    <div key={e.id} className={`px3-event px3-event--${e.level}`}>
                      <span className="px3-event-ts">{fmtDate(e.ts)}</span>
                      <span className="px3-event-stage">{e.stage ?? '-'}</span>
                      <span className="px3-event-msg">{e.message}</span>
                    </div>
                  ),
                )}
              </div>
            )}
          </div>
        </section>
      )}

      {confirming && confirmSpec && (
        <div className="px3-modal-overlay">
          <div className="px3-modal">
            <div className="px3-modal-header">
              <h3 className="px3-modal-title">{confirmSpec.title}</h3>
            </div>
            <div className="px3-modal-body">
              <p>{confirmSpec.body}</p>
              <p className="px3-cell-faint">
                {order.customer} - {order.external_ref} - stage {order.stage}
              </p>
            </div>
            <div className="px3-modal-footer">
              <button
                type="button"
                className="px3-btn px3-btn--danger"
                disabled={busy !== null}
                onClick={() => void runAction(confirming)}
              >
                {busy === confirming ? 'Working...' : confirmSpec.cta}
              </button>
              <button
                type="button"
                className="px3-btn px3-btn--secondary"
                disabled={busy !== null}
                onClick={() => setConfirming(null)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
